import classNames from 'classnames';
import { useId } from 'react';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import IconRenderer, { IconDefinition } from '../IconRenderer';
import Tooltip from '../Tooltip';
import styles from './styles/styles.module.scss';

type SegmentedControlOption<T extends string> = { id: T; label: string } | { id: T; icon: IconDefinition<'orientation'>; title?: string };

type SegmentedControlProps<T extends string> = {
  activeId: T;
  ariaLabel: string;
  className?: string;
  onChange: (id: T) => void;
  options: SegmentedControlOption<T>[];
  variant?: 'default' | 'transparent';
  width?: 'auto' | 'full';
};

export default function SegmentedControl<T extends string>({
  activeId,
  ariaLabel,
  className,
  onChange,
  options,
  variant = 'default',
  width = 'auto'
}: SegmentedControlProps<T>) {
  const baseId = useId();
  const groupName = `${baseId}-group`;

  return (
    <fieldset {...setDenaliAttribute('SegmentedControl')} className={classNames(styles.container, styles[width], styles[variant], className)}>
      <legend className="screenReadersOnly">{ariaLabel}</legend>
      {options.map((option, index) => {
        const { id } = option;
        const isActive = activeId === id;
        const isIcon = 'icon' in option;
        const inputId = `${baseId}-${index}`;
        const labelElement = (
          <>
            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
            <input
              key={id}
              type="radio"
              id={inputId}
              name={groupName}
              value={id}
              checked={isActive}
              onChange={() => onChange(id)}
              className={styles.input}
            />
            <label
              htmlFor={inputId}
              className={classNames(styles.option, { [styles.active]: isActive, [styles.icon]: isIcon })}
              aria-label={isIcon ? option.title : undefined}
            >
              {isIcon ? <IconRenderer icon={option.icon} defaults={{ color: 'currentColor' }} /> : <span>{option.label}</span>}
            </label>
          </>
        );

        if (isIcon && option.title) {
          return (
            <div key={`${id}-wrapper`} className={styles.tooltipContainer}>
              <Tooltip anchorContainerClassName={styles.tooltipContainer} anchorElement={labelElement} text={option.title} />
            </div>
          );
        }

        return (
          <div key={`${id}-wrapper`} className={styles.optionWrapper}>
            {labelElement}
          </div>
        );
      })}
    </fieldset>
  );
}
